/**
 * Config สำหรับ retrieve-source ที่มี pattern "checkbox + URL(+Token) input"
 * เดิม pattern นี้ (API/Azure/Google/Facebook) copy-paste กัน 4 รอบในแต่ละไฟล์
 * และซ้ำข้าม CreatePortal.jsx / EditPortal.jsx ด้วย
 *
 * ไม่รวม OneID เพราะมี sub-field เฉพาะ (เลขบัตร/อีเมล/เลขสมาชิก) คนละ pattern
 * ไม่รวม OneCollab เพราะของเดิมไม่มี UI ให้กรอก URL/Token สำหรับ OneCollab อยู่แล้ว
 *
 * สังเกตความไม่สม่ำเสมอที่ตั้งใจคงไว้ตามของเดิม (ไม่ได้แก้ให้ตรงกันเอง เพราะเป็น
 * การเปลี่ยน behavior/payload ที่ต้อง confirm ก่อน):
 * - API: ตอนติ๊กเปิด ใช้ค่า link/token ปัจจุบันเป็นค่าเริ่มต้นของ retrieve entry
 * - Azure/Google/Facebook: ตอนติ๊กเปิด บังคับ url เป็น null เสมอ (ไม่สนใจค่าที่ค้างอยู่)
 *   และ entry ที่ส่งไป backend มี key `token: null` ติดไปด้วยเสมอ ทั้งที่ UI ไม่มีช่องกรอก token
 * - errorText ของ Azure/Google/Facebook ใช้ข้อความ "กรุณาเลือกวิธีการเข้าสู่ระบบ"
 *   (ของเดิมน่าจะพิมพ์ผิด/ก็อปมาจากส่วนอื่น ไม่ใช่ "กรุณากรอก URL") — คงไว้ตามเดิม
 * - marginBottom ของ wrapper: Azure/Google มี marginBottom 15px, Facebook ไม่มี (ของเดิมไม่เท่ากัน)
 */
export const buildRetrieveUrlSources = ({
    link, setLink, token, setToken,
    useClientSecret, setUseClientSecret,
    clientKey, setClientKey,
    secretKey, setSecretKey,
    azureurl, setAzureURL, googleurl, setGoogleURL, facebookurl, setFacebookURL
}) => [
    {
        key: 'API',
        label: 'ดึงข้อมูลจาก API',
        requiresLoginMethod: false,
        fields: [
            {
                name: 'url',
                label: 'URL',
                value: link,
                onValueChange: setLink,
                placeholder: 'URL-1234567890',
                errorText: 'กรุณากรอก URL',
                extraInputStyle: { marginRight: '10px' },
                alertMarginTop: '5px',
                wrapperStyle: { marginLeft: '30px' },
            },
            {
                name: 'token',
                label: 'Token',
                value: token,
                onValueChange: setToken,
                placeholder: 'Token-1234567890',
                errorText: 'กรุณากรอก Token',
                extraInputStyle: {},
                alertMarginTop: '5px',
                wrapperStyle: { marginLeft: '30px' },
                // Token ไม่บังคับกรอกอีกต่อไป ถ้าติ๊กใช้ Client Key/Secret Key แทน
                required: (entry) => !entry?.useClientSecret,
            },
            {
                type: 'checkbox',
                name: 'useClientSecret',
                label: 'ใช้ข้อมูลจาก Client Key และ Secret Key',
                value: useClientSecret,
                onValueChange: setUseClientSecret,
                wrapperStyle: { marginLeft: '30px', marginTop: '10px' },
                // ติ๊กออกแล้วเคลียร์ Client Key/Secret Key ทิ้งด้วย ไม่ปล่อยค้าง
                clearFieldsOnUncheck: ['clientKey', 'secretKey'],
            },
            {
                name: 'clientKey',
                label: 'Client Key',
                value: clientKey,
                onValueChange: setClientKey,
                placeholder: 'Client Key',
                errorText: 'กรุณากรอก Client Key',
                extraInputStyle: { marginRight: '10px' },
                alertMarginTop: '5px',
                wrapperStyle: { marginLeft: '30px' },
                hidden: (entry) => !entry?.useClientSecret,
                required: (entry) => !!entry?.useClientSecret,
            },
            {
                name: 'secretKey',
                label: 'Secret Key',
                value: secretKey,
                onValueChange: setSecretKey,
                placeholder: 'Secret Key',
                errorText: 'กรุณากรอก Secret Key',
                extraInputStyle: {},
                alertMarginTop: '5px',
                wrapperStyle: { marginLeft: '30px' },
                hidden: (entry) => !entry?.useClientSecret,
                required: (entry) => !!entry?.useClientSecret,
            },
        ],
    },
    {
        key: 'Azure',
        label: 'ดึงข้อมูลจาก Azure',
        requiresLoginMethod: true,
        staticFields: { token: null }, // ของเดิมส่ง token: null ติดไปด้วยเสมอ แม้ UI ไม่มีช่องกรอก
        fields: [
            {
                name: 'url',
                label: 'URL',
                value: azureurl,
                onValueChange: setAzureURL,
                placeholder: 'URL-1234567890',
                errorText: 'กรุณากรอก URL',
                extraInputStyle: { marginRight: '10px' },
                alertMarginTop: '5px',
                wrapperStyle: { marginLeft: '30px' },
                initialValueOnEnable: null, // ของเดิมบังคับ null ตอนติ๊กเปิด ไม่สนใจค่าที่ค้างอยู่
            },
        ],
    },
    {
        key: 'Google',
        label: 'ดึงข้อมูลจาก Google',
        requiresLoginMethod: true,
        staticFields: { token: null },
        fields: [
            {
                name: 'url',
                label: 'URL',
                value: googleurl,
                onValueChange: setGoogleURL,
                placeholder: 'URL-1234567890',
                errorText: 'กรุณากรอก URL',
                extraInputStyle: { marginRight: '10px' },
                alertMarginTop: '15px',
                wrapperStyle: { marginLeft: '30px' },
                initialValueOnEnable: null,
            },
        ],
    },
    {
        key: 'Facebook',
        label: 'ดึงข้อมูลจาก Facebook',
        requiresLoginMethod: true,
        staticFields: { token: null },
        fields: [
            {
                name: 'url',
                label: 'URL',
                value: facebookurl,
                onValueChange: setFacebookURL,
                placeholder: 'URL-1234567890',
                errorText: 'กรุณากรอก URL',
                extraInputStyle: { marginRight: '10px' },
                alertMarginTop: '15px',
                wrapperStyle: { marginLeft: '30px' }, // ไม่มี marginBottom ตามของเดิม
                initialValueOnEnable: null,
            },
        ],
    },
];